import React, { useCallback, useState, useRef, useLayoutEffect } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { debounce, find, get as _get, lowerCase } from 'lodash'
import { Empty, Input, Select, Spin } from 'antd'
import {
  EduButton,
  CustomModalStyled,
  FlexContainer,
  RadioBtn,
  RadioGrp,
  SelectInputStyled,
  notification,
} from '@edulastic/common'
import { mobileWidthMax, themeColor } from '@edulastic/colors'
import { folderSharing, folderTypes, roleuser } from '@edulastic/constants'
import styled from 'styled-components'
import {
  receiveRenameFolderAction,
  receiveCreateFolderAction,
  toggleMoveItemsFolderAction,
} from '../../actions/folder'
import {
  isOpenAddItemsModalSelector,
  getFoldersSelector,
  getSelectedItems,
} from '../../selectors/folder'
import { getOrgDataSelector, getUserOrgId } from '../../selectors/user'
import {
  fetchUsersListAction,
  getFetchingSelector,
  getUsersListSelector,
  updateUsersListAction,
} from '../../../sharedDucks/userDetails'
import { getFullNameFromAsString } from '../../../../common/utils/helpers'
import { RadioInputWrapper } from '../common/RadioInput'
import { ModalTitle } from './styled'
import {
  buildSharePayload,
  GIVE_ACCESS_MODE,
  getDistrictViewerForShare,
} from './folderShareHelpers'

const { FOLDER_PERMISSION } = folderSharing
const INDIVIDUAL_SEARCH_TYPE = 'INDIVIDUAL'

const isSameUserId = (left, right) =>
  left != null && right != null && String(left) === String(right)

const getUserSource = (user) => user?._source || user || {}

const NotFoundContent = ({ isSearching }) =>
  isSearching ? (
    <Spin size="small" />
  ) : (
    <Empty
      className="ant-empty-small"
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      style={{ textAlign: 'left', margin: '10px 0' }}
      description="No match found"
    />
  )

const ExtendedInput = ({ value, onChange, visible, onKeyUp, placeholder }) => {
  const renameInput = useRef()
  useLayoutEffect(() => {
    renameInput.current.select()
  }, [visible])

  const handleChange = (e) => {
    if (onChange) {
      onChange(e.target.value)
    }
  }

  return (
    <Input
      style={{ 'border-color': themeColor }}
      placeholder={placeholder || 'Name this folder'}
      value={value}
      onChange={handleChange}
      ref={renameInput}
      onKeyUp={onKeyUp}
      maxLength={32}
    />
  )
}

const AddModal = ({
  folder,
  folders,
  folderType,
  closeModal,
  isOpenAddModal,
  closeMoveModal,
  renameFolder,
  createFolderRequest,
  isRename,
  selectedItems,
  addToFolderFlow,
  canShare = false,
  usersList,
  fetchingUsers,
  currentUserId,
  userOrgData,
  districtId,
  fetchUsers,
  updateShareList,
}) => {
  const isEdit = !!folder?._id && isRename
  const showShareOnCreate = canShare && !isRename
  const initFolderName = isEdit ? folder?.folderName : ''
  const [folderName, setFolderName] = useState(initFolderName)
  const [giveAccessMode, setGiveAccessMode] = useState(
    GIVE_ACCESS_MODE.INDIVIDUALS
  )
  const [selectedUsers, setSelectedUsers] = useState([])
  const [givePermission, setGivePermission] = useState(FOLDER_PERMISSION.EDITOR)
  const [isShareExpanded, setIsShareExpanded] = useState(false)

  const searchUser = useCallback(
    debounce((value) => {
      fetchUsers({
        limit: 10,
        page: 1,
        type: INDIVIDUAL_SEARCH_TYPE,
        districtId,
        search: {
          role: ['teacher', 'school-admin', 'district-admin'],
          searchString: value,
          status: 1,
        },
      })
    }, 500),
    [fetchUsers, districtId]
  )

  const handleMultiUserSearch = (value) => {
    if (value.length > 2) {
      searchUser(value)
    } else {
      updateShareList({ data: [] })
    }
  }

  const onUserSelect = (userId) => {
    if (selectedUsers.find((o) => o._id === userId)) return
    const user = (usersList || []).find((d) => d._id === userId)
    if (!user || isSameUserId(user._id, currentUserId)) {
      return
    }
    setSelectedUsers([...selectedUsers, user])
  }

  const onUserDeselect = (userId) => {
    setSelectedUsers(selectedUsers.filter((o) => o._id !== userId))
  }

  const handleRadioChange = (e) => {
    const mode = e.target.value
    setGiveAccessMode(mode)
    if (mode === GIVE_ACCESS_MODE.DISTRICT) {
      setSelectedUsers([])
      updateShareList({ data: [] })
    }
  }

  const handleCloseModal = () => {
    if (isOpenAddModal) {
      closeMoveModal({
        items: [],
        isOpen: false,
      })
    }
    closeModal()
  }

  const handleAddClick = () => {
    const isExist = find(
      folders,
      (f) => lowerCase(f.folderName) === lowerCase(folderName)
    )
    if (isExist) {
      return notification({ messageKey: 'folderNameAlreadyUsed' })
    }

    if (isEdit && renameFolder) {
      renameFolder({ folderId: folder._id, folderName, folderType })
    } else if (createFolderRequest) {
      const data = {
        folderName,
        folderType,
      }
      if (
        !isOpenAddModal &&
        folderType === folderTypes.ASSIGNMENT &&
        selectedItems.length &&
        addToFolderFlow
      ) {
        data.content = selectedItems.map((item) => ({
          _id: item.itemId,
          contentType: folderTypes.TEST,
        }))
      }
      if (canShare) {
        const usersForShare = selectedUsers
          .filter((user) => !isSameUserId(user._id, currentUserId))
          .map((user) => {
            const source = getUserSource(user)
            return {
              _id: user._id,
              name: getFullNameFromAsString(source).trim() || source.email,
              email: source.email,
              permission: givePermission,
            }
          })
        const districtViewer = getDistrictViewerForShare({
          giveAccessMode,
          districtInAccessList: false,
          districtRemovedFromAccess: false,
        })
        Object.assign(data, buildSharePayload(usersForShare, districtViewer))
      }
      createFolderRequest(data)
    }

    closeModal()
  }

  const handleCreateOnKeyPress = (e) => {
    if (e.keyCode === 13 && folderName?.length) {
      handleAddClick()
    }
  }

  const { districts = [{}] } = userOrgData || {}
  const { districtName = '' } =
    districts.find((d) => d.districtId === districtId) || {}
  const districtLabel = districtName
    ? `Anyone in ${districtName}`
    : 'Anyone in district'

  const filteredUserList = (usersList || []).filter((user) => {
    if (isSameUserId(user._id, currentUserId)) {
      return false
    }
    return true
  })
  const multiSelectUserList = [
    ...filteredUserList,
    ...selectedUsers.filter(
      (user) => !filteredUserList.some((u) => u._id === user._id)
    ),
  ]

  return (
    <CustomModalStyled
      centered
      visible
      title={
        <ModalTitle>{isEdit ? 'Rename' : 'Create a New Folder'}</ModalTitle>
      }
      onCancel={handleCloseModal}
      footer={[
        <EduButton
          isGhost
          data-cy="cancel"
          key="back"
          variant="create"
          onClick={handleCloseModal}
        >
          Cancel
        </EduButton>,
        <EduButton
          data-cy="submit"
          key="submit"
          color="primary"
          variant="create"
          disabled={!folderName}
          onClick={handleAddClick}
        >
          {isEdit ? `Update Folder` : 'Create New Folder'}
        </EduButton>,
      ]}
    >
      {isOpenAddModal && <h4>No folders have been created.</h4>}
      {showShareOnCreate ? (
        <>
          <FieldLabel>FOLDER NAME</FieldLabel>
          <ExtendedInput
            value={folderName}
            onChange={setFolderName}
            visible={false}
            onKeyUp={handleCreateOnKeyPress}
            placeholder="Enter name here"
          />
          <ShareSectionTitle
            role="button"
            tabIndex={0}
            aria-expanded={isShareExpanded}
            aria-label={
              isShareExpanded
                ? 'Collapse share folder access'
                : 'Expand share folder access'
            }
            data-cy="folder-create-share-toggle"
            onClick={() => setIsShareExpanded((open) => !open)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                setIsShareExpanded((open) => !open)
              }
            }}
          >
            <ShareToggleIcon aria-hidden>
              {isShareExpanded ? '—' : '+'}
            </ShareToggleIcon>
            SHARE FOLDER ACCESS
          </ShareSectionTitle>
          {isShareExpanded && (
            <PeopleBlock>
              <RadioBtnWrapper>
                <RadioGrp value={giveAccessMode} onChange={handleRadioChange}>
                  <RadioBtn value={GIVE_ACCESS_MODE.DISTRICT}>
                    DISTRICT
                  </RadioBtn>
                  <RadioBtn value={GIVE_ACCESS_MODE.INDIVIDUALS}>
                    INDIVIDUALS
                  </RadioBtn>
                </RadioGrp>
              </RadioBtnWrapper>
              {giveAccessMode === GIVE_ACCESS_MODE.INDIVIDUALS ? (
                <FlexContainer
                  style={{ marginTop: 5, position: 'relative' }}
                  justifyContent="flex-start"
                  alignItems="flex-start"
                >
                  <UserMultiSelectWrapper>
                    <UserMultiSelectStyled
                      mode="multiple"
                      bg="white"
                      height="40px"
                      optionLabelProp="chipLabel"
                      placeholder="Enter names or email addresses"
                      value={selectedUsers.map((x) => x._id)}
                      onSearch={handleMultiUserSearch}
                      loading={fetchingUsers}
                      onSelect={onUserSelect}
                      onDeselect={onUserDeselect}
                      dropdownClassName="custom-antd-select"
                      filterOption={false}
                      notFoundContent={
                        <NotFoundContent isSearching={fetchingUsers} />
                      }
                      getPopupContainer={(triggerNode) =>
                        triggerNode.parentNode
                      }
                      data-cy="folder-create-user-search"
                    >
                      {multiSelectUserList.map((user) => {
                        const source = getUserSource(user)
                        const name = `${source.firstName || ''} ${
                          source.lastName || ''
                        }`.trim()
                        const email = source.email || ''
                        const role = source.role
                        return (
                          <Select.Option
                            key={user._id}
                            value={user._id}
                            chipLabel={name || email}
                            title={`${name} (${email})${
                              role ? ` [${roleuser.ROLE_LABEL[role]}]` : ''
                            }`}
                          >
                            <span>
                              {name}
                              {email ? `(${email})` : ''}{' '}
                              {role ? (
                                <b>{`[${roleuser.ROLE_LABEL[role]}]`}</b>
                              ) : null}
                            </span>
                          </Select.Option>
                        )
                      })}
                    </UserMultiSelectStyled>
                  </UserMultiSelectWrapper>
                  <PermissionSelectWrapper>
                    <GiveAccessSelect
                      onChange={setGivePermission}
                      data-cy="folder-create-give-permission"
                      value={givePermission}
                      getPopupContainer={(triggerNode) =>
                        triggerNode.parentNode
                      }
                    >
                      <Select.Option value={FOLDER_PERMISSION.VIEWER}>
                        Viewer
                      </Select.Option>
                      <Select.Option value={FOLDER_PERMISSION.EDITOR}>
                        Editor
                      </Select.Option>
                    </GiveAccessSelect>
                  </PermissionSelectWrapper>
                </FlexContainer>
              ) : (
                <ShareMessageWrapper data-cy="folderCreateDistrictMessage">
                  {`${districtLabel} as Viewer`}
                </ShareMessageWrapper>
              )}
            </PeopleBlock>
          )}
        </>
      ) : (
        <ExtendedInput
          value={folderName}
          onChange={setFolderName}
          visible={isEdit}
          onKeyUp={handleCreateOnKeyPress}
        />
      )}
    </CustomModalStyled>
  )
}

AddModal.propTypes = {
  canShare: PropTypes.bool,
  usersList: PropTypes.array,
  fetchingUsers: PropTypes.bool,
  currentUserId: PropTypes.string,
  userOrgData: PropTypes.object,
  districtId: PropTypes.string,
  fetchUsers: PropTypes.func,
  updateShareList: PropTypes.func,
}

AddModal.defaultProps = {
  canShare: false,
  usersList: [],
  fetchingUsers: false,
  currentUserId: '',
  userOrgData: {},
  districtId: '',
  fetchUsers: () => null,
  updateShareList: () => null,
}

export default connect(
  (state) => ({
    folders: getFoldersSelector(state),
    isOpenAddModal: isOpenAddItemsModalSelector(state),
    selectedItems: getSelectedItems(state),
    usersList: getUsersListSelector(state),
    fetchingUsers: getFetchingSelector(state),
    currentUserId: _get(state, 'user.user._id', ''),
    userOrgData: getOrgDataSelector(state),
    districtId: getUserOrgId(state),
  }),
  {
    createFolderRequest: receiveCreateFolderAction,
    renameFolder: receiveRenameFolderAction,
    addMoveToFolderRequest: receiveCreateFolderAction,
    closeMoveModal: toggleMoveItemsFolderAction,
    fetchUsers: fetchUsersListAction,
    updateShareList: updateUsersListAction,
  }
)(AddModal)

const FieldLabel = styled.div`
  margin-bottom: 8px;
  text-transform: uppercase;
  font-size: 12px;
  font-weight: 600;
  color: ${themeColor};
`

const ShareSectionTitle = styled.div`
  margin-top: 20px;
  text-transform: uppercase;
  font-size: 13px;
  font-weight: 600;
  color: ${themeColor};
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  user-select: none;
  width: fit-content;

  &:focus {
    outline: 2px solid ${themeColor};
    outline-offset: 2px;
  }
`

const ShareToggleIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 12px;
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
`

const PeopleBlock = styled.div`
  margin-top: 8px;
  display: flex;
  flex-direction: column;

  .ant-radio {
    margin-right: 5px;
  }
`

const RadioBtnWrapper = styled(RadioInputWrapper)`
  font-weight: 600;
  margin: 10px 0px;
`

const ShareMessageWrapper = styled.div`
  height: 40px;
  line-height: 40px;
  flex: 1 1 0;
  min-width: 0;
  font-size: 13px;
`

const UserMultiSelectWrapper = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  margin-right: 10px;
  align-self: flex-start;
`

const PermissionSelectWrapper = styled.div`
  flex: 0 0 140px;
  width: 140px;
  max-width: 140px;
  align-self: flex-start;

  .ant-select {
    width: 100%;
    height: 40px;

    .ant-select-selection {
      box-sizing: border-box;
      height: 40px;
      min-height: 40px;
    }

    .ant-select-selection--single .ant-select-selection__rendered {
      line-height: 38px;
    }
  }

  @media (max-width: ${mobileWidthMax}) {
    width: 100%;
    max-width: 100%;
    margin-top: 5px;
  }
`

const GiveAccessSelect = styled(SelectInputStyled)`
  &.ant-select {
    @media (max-width: ${mobileWidthMax}) {
      width: 100%;
      margin-top: 5px;
    }
  }
`

const UserMultiSelectStyled = styled(SelectInputStyled)`
  &.ant-select {
    width: 100%;
    vertical-align: top;
    min-height: 40px;
    max-height: 110px;
    height: auto;

    &:not(:has(.ant-select-selection__choice)) {
      height: 40px;
    }

    .ant-select-selection.ant-select-selection--multiple {
      align-items: center;
      box-sizing: border-box;
      height: auto !important;
      min-height: 40px;
      max-height: 110px;
      overflow-y: auto;
    }

    &:not(:has(.ant-select-selection__choice))
      .ant-select-selection.ant-select-selection--multiple {
      height: 40px !important;
    }

    .ant-select-selection__choice__content {
      text-transform: none;
    }
  }
`
